import { lazy, Suspense, ComponentType, ReactNode } from 'react'
import { useParams } from 'react-router-dom'

interface UseDocContentOptions {
  fallback?: ReactNode
  docPath: string
}

// Pre-load all MDX files using Vite's import.meta.glob
// This creates a static map that Vite can analyze at build time
const mdxModules = import.meta.glob('../content/docs/**/*.mdx')

/**
 * Hook to dynamically load versioned documentation MDX content
 */
export function useDocContent({ docPath, fallback }: UseDocContentOptions): ComponentType {
  const { version = 'latest', lang = 'en' } = useParams()

  const MDXComponent = lazy(async () => {
    // Try the requested version and language
    let modulePath = `../content/docs/${version}/${lang}/${docPath}.mdx`
    let loader = mdxModules[modulePath]

    // Fallback to English if language not found
    if (!loader) {
      console.warn(`MDX file ${modulePath} not found, trying English`)
      modulePath = `../content/docs/${version}/en/${docPath}.mdx`
      loader = mdxModules[modulePath]
    }

    // Fallback to latest version
    if (!loader) {
      console.warn(`Falling back to latest version for ${docPath}`)
      modulePath = `../content/docs/latest/en/${docPath}.mdx`
      loader = mdxModules[modulePath]
    }

    // If still not found, throw error
    if (!loader) {
      throw new Error(`Documentation file not found: ${docPath}`)
    }

    return loader() as Promise<{ default: ComponentType }>
  })

  return () => (
    <Suspense fallback={fallback || <div className='loading'>Loading documentation...</div>}>
      <MDXComponent />
    </Suspense>
  )
}
